import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FaUsers, FaUserCheck, FaQuestionCircle, FaCheckCircle, FaClock,
  FaClipboardList, FaArrowRight, FaUserPlus, FaBuilding, FaFileAlt,
  FaSync, FaTasks
} from "react-icons/fa";
import axios from "axios";
import toastr from "toastr";
import "toastr/build/toastr.min.css";

const HomeForCompanyAdmin = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [companyStats, setCompanyStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    inactiveUsers: 0,
    totalQuestions: 0,
    answeredQuestions: 0,
    inProgressQuestions: 0,
    completionRate: 0
  });
  const [recentUsers, setRecentUsers] = useState([]);
  const [userName, setUserName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [isFormFinished, setIsFormFinished] = useState(false);

  useEffect(() => {
    // Get user name from localStorage
    const name = localStorage.getItem('name') || 'Company Admin';
    setUserName(name);

    // Fetch company admin's data
    fetchCompanyData();

    // Check if form is already finished
    checkIfFormFinished();
  }, []);

  const fetchCompanyData = async (showToast = false) => {
    if (showToast) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    try {
      // Try to get company info from step1 first
      let companyId = null;
      try {
        const step1Response = await axios.get('/step1');
        if (step1Response.data && step1Response.data.step1) {
          setCompanyName(step1Response.data.step1.name);
          companyId = step1Response.data.step1.id || step1Response.data.step1.companyId;
        }
      } catch (step1Error) {
        console.error('Error fetching company name:', step1Error);
        // Continue without company name
      }

      // Fetch users created by this company admin
      const usersResponse = await axios.get('/api/users?page=1&limit=1000');
      const allUsers = usersResponse.data?.users || [];
      const totalItemsFromAPI = usersResponse.data?.totalItems || allUsers.length;

      // Use the exact same logic as UsersTable.jsx
      let totalUsers = totalItemsFromAPI;
      // Include the company admin themselves (same logic as UsersTable)
      totalUsers += 1;

      // Calculate active users the same way as UsersTable.jsx (count all active users in system)
      const isUserActive = (user) => !user.isDeleted && !user.isFirstLogin;
      let activeUsers = allUsers.filter(user => isUserActive(user)).length;

      // Add +1 for the company admin (they're always considered active since they're logged in)
      const loggedInUserRole = localStorage.getItem('role');
      if (loggedInUserRole === 'companyAdmin') {
        activeUsers += 1;
      }

      // For inactive users, count users created by this company admin
      // Note: Backend already filters by createdBy, so all users returned are company users
      const companyUsers = allUsers.filter(user =>
        user.role === 'user' &&
        !user.isDeleted
      );
      const inactiveUsers = companyUsers.filter(user => user.isFirstLogin).length;

      // Get recent users (last 5, sorted by createdAt)
      const recent = companyUsers
        .sort((a, b) => {
          const dateA = new Date(a.createdAt || 0);
          const dateB = new Date(b.createdAt || 0);
          return dateB - dateA;
        })
        .slice(0, 5);

      setRecentUsers(recent);

      // Fetch reporting summary for this company (all users in the company)
      let totalQuestions = 0;
      let answeredQuestions = 0;
      let inProgressQuestions = 0;
      let completionRate = 0;

      try {
        // Get summary for all users in the company instead of just the current user
        const summaryUrl = companyId ? `/summary?companyId=${companyId}` : '/summary';
        console.log('=== HOME DASHBOARD: Fetching summary ===');
        console.log('Company ID:', companyId);
        console.log('Summary URL:', summaryUrl);

        const summaryResponse = await axios.get(summaryUrl);
        console.log('Summary response:', summaryResponse.data);

        if (summaryResponse.data.status && summaryResponse.data.summary) {
          const summary = summaryResponse.data.summary;
          totalQuestions = summary.totalQuestions || 0;
          answeredQuestions = summary.totalAnswered || 0;
          inProgressQuestions = (summary.totalQuestions || 0) - (summary.totalAnswered || 0);
          completionRate = totalQuestions > 0
            ? Math.round((answeredQuestions / totalQuestions) * 100)
            : 0;

          console.log('Calculated stats:', {
            totalQuestions,
            answeredQuestions,
            inProgressQuestions,
            completionRate
          });
        }
      } catch (summaryError) {
        console.error('Error fetching summary:', summaryError);
        // Continue without summary data
      }

      setCompanyStats({
        totalUsers,
        activeUsers,
        inactiveUsers,
        totalQuestions,
        answeredQuestions,
        inProgressQuestions,
        completionRate
      });
      
      if (showToast) {
        toastr.success(t('toasts.dataRefreshed'));
      }
    } catch (error) {
      console.error('Error fetching company data:', error);
      const errorMessage = error.response?.data?.message || 'Failed to load company data. Please try again.';
      toastr.error(errorMessage);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const checkIfFormFinished = async () => {
    try {
      const response = await axios.post('/is-form-finished');
      if (response.data.status) {
        setIsFormFinished(response.data.isFormFinished);
      }
    } catch (error) {
      console.error('Error checking form status:', error);
    }
  };

  const handleRefresh = () => {
    fetchCompanyData(true);
  };

  const handleAddUser = () => {
    navigate('/users?openModal=true');
  };

  const handleManageUsers = () => {
    navigate('/users');
  };

  const handleViewReports = () => {
    navigate('/reporting');
  };

  const handleAssignQuestions = () => {
    navigate('/questions');
  };

  const handleRegisterCompany = () => {
    navigate('/register-company');
  };

  // Skeleton loading component
  const SkeletonCard = () => (
    <div className="bg-white rounded-lg shadow p-4 border border-gray-200 animate-pulse">
      <div className="flex items-center justify-between">
        <div className="space-y-2 flex-1">
          <div className="h-3 bg-gray-200 rounded w-1/2"></div>
          <div className="h-6 bg-gray-200 rounded w-3/4"></div>
          <div className="h-2 bg-gray-200 rounded w-1/3"></div>
        </div>
        <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 font-sans antialiased">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 shadow-lg">
        <div className="max-w-7xl 2xl:max-w-[90rem] 3xl:max-w-[100rem] 4xl:max-w-[120rem] mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">
                {t('dashboard.welcome')}, {userName}!
              </h1>
              {companyName && (
                <p className="text-blue-100 text-sm sm:text-base mb-1">
                  {companyName}
                </p>
              )}
              <p className="text-blue-100 text-sm sm:text-base">
                {t('dashboard.manageTeam')}
              </p>
            </div>
            <div className="mt-4 sm:mt-0 flex items-center gap-3">
              <button
                onClick={handleRefresh}
                disabled={refreshing || loading}
                className="bg-white/20 text-white px-3 py-2 rounded-lg font-semibold hover:bg-white/30 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Refresh data"
              >
                <FaSync className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                {refreshing ? t('dashboard.refreshing') : t('common.refresh')}
              </button>
              <button
                onClick={handleAddUser}
                className="bg-white text-blue-600 px-4 py-2 rounded-lg font-semibold hover:bg-blue-50 transition-colors flex items-center gap-2 shadow-md"
              >
                <FaUserPlus className="w-4 h-4" />
{t('dashboard.addNewUser')}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl 2xl:max-w-[90rem] 3xl:max-w-[100rem] 4xl:max-w-[120rem] mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Statistics Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {loading ? (
            <>
              <SkeletonCard />
              <SkeletonCard />
              <SkeletonCard />
              <SkeletonCard />
            </>
          ) : (
            <>
              {/* Total Users */}
              <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">{t('dashboard.totalUsers')}</p>
                    <p className="text-2xl font-bold text-blue-600">{companyStats.totalUsers}</p>
                    <p className="text-xs text-gray-500 mt-1">{t('dashboard.teamMembers')}</p>
                  </div>
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <FaUsers className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
              </div>

              {/* Active Users */}
              <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">{t('dashboard.activeUsers')}</p>
                    <p className="text-2xl font-bold text-green-600">{companyStats.activeUsers}</p>
                    <p className="text-xs text-gray-500 mt-1">{t('dashboard.currentlyActive')}</p>
                  </div>
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <FaUserCheck className="w-6 h-6 text-green-600" />
                  </div>
                </div>
              </div>

              {/* Total Questions */}
              <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">{t('dashboard.totalAssigned')}</p>
                    <p className="text-2xl font-bold text-purple-600">{companyStats.totalQuestions}</p>
                    <p className="text-xs text-gray-500 mt-1">{t('dashboard.assigned')}</p>
                  </div>
                  <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                    <FaQuestionCircle className="w-6 h-6 text-purple-600" />
                  </div>
                </div>
              </div>

              {/* Completion Rate */}
              <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">{t('dashboard.completionRate')}</p>
                    <p className="text-2xl font-bold text-indigo-600">{companyStats.completionRate}%</p>
                    <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                      <div
                        className="bg-indigo-600 h-2 rounded-full transition-all"
                        style={{ width: `${companyStats.completionRate}%` }}
                      ></div>
                    </div>
                  </div>
                  <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center">
                    <FaCheckCircle className="w-6 h-6 text-indigo-600" />
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Additional Stats Row */}
        {!loading && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            {/* Answered Questions */}
            <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">{t('dashboard.answered')}</p>
                  <p className="text-2xl font-bold text-green-600">{companyStats.answeredQuestions}</p>
                  <p className="text-xs text-gray-500 mt-1">{t('dashboard.completedByTeam')}</p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <FaCheckCircle className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </div>

            {/* In Progress Questions */}
            <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">{t('dashboard.inProgress')}</p>
                  <p className="text-2xl font-bold text-yellow-600">{companyStats.inProgressQuestions}</p>
                  <p className="text-xs text-gray-500 mt-1">{t('dashboard.pendingAnswers')}</p>
                </div>
                <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                  <FaClock className="w-6 h-6 text-yellow-600" />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <div className="bg-white rounded-lg shadow p-4 sm:p-6 mb-6 border border-gray-200">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mb-4 flex items-center">
            <FaClipboardList className="w-5 h-5 mr-2 text-blue-600" />
            {t('dashboard.quickActions')}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <button
              onClick={handleAddUser}
              className="p-4 rounded-lg border-2 border-blue-300 bg-blue-50 hover:bg-blue-100 transition-all hover:shadow-md text-left"
            >
              <div className="flex items-center justify-between mb-2">
                <FaUserPlus className="w-6 h-6 text-blue-600" />
                <FaArrowRight className="w-4 h-4 text-blue-600" />
              </div>
              <h3 className="font-semibold text-gray-800 mb-1">{t('dashboard.addNewUser')}</h3>
              <p className="text-sm text-gray-600">{t('dashboard.inviteTeamMember')}</p>
            </button>

            <button
              onClick={handleManageUsers}
              className="p-4 rounded-lg border-2 border-green-300 bg-green-50 hover:bg-green-100 transition-all hover:shadow-md text-left"
            >
              <div className="flex items-center justify-between mb-2">
                <FaUsers className="w-6 h-6 text-green-600" />
                <FaArrowRight className="w-4 h-4 text-green-600" />
              </div>
              <h3 className="font-semibold text-gray-800 mb-1">{t('dashboard.manageUsers')}</h3>
              <p className="text-sm text-gray-600">{t('dashboard.viewTeam')}</p>
            </button>

            {!isFormFinished && (
              <button
                onClick={handleRegisterCompany}
                className="p-4 rounded-lg border-2 border-teal-300 bg-teal-50 hover:bg-teal-100 transition-all hover:shadow-md text-left"
              >
                <div className="flex items-center justify-between mb-2">
                  <FaBuilding className="w-6 h-6 text-teal-600" />
                  <FaArrowRight className="w-4 h-4 text-teal-600" />
                </div>
                <h3 className="font-semibold text-gray-800 mb-1">{t('navigation.registerCompany')}</h3>
                <p className="text-sm text-gray-600">{t('dashboard.completeKYC')}</p>
              </button>
            )}

            <button
              onClick={handleAssignQuestions}
              className="p-4 rounded-lg border-2 border-purple-300 bg-purple-50 hover:bg-purple-100 transition-all hover:shadow-md text-left"
            >
              <div className="flex items-center justify-between mb-2">
                <FaTasks className="w-6 h-6 text-purple-600" />
                <FaArrowRight className="w-4 h-4 text-purple-600" />
              </div>
              <h3 className="font-semibold text-gray-800 mb-1">{t('dashboard.assignQuestions')}</h3>
              <p className="text-sm text-gray-600">{t('dashboard.assignToUsers')}</p>
            </button>

            <button
              onClick={handleViewReports}
              className="p-4 rounded-lg border-2 border-indigo-300 bg-indigo-50 hover:bg-indigo-100 transition-all hover:shadow-md text-left"
            >
              <div className="flex items-center justify-between mb-2">
                <FaFileAlt className="w-6 h-6 text-indigo-600" />
                <FaArrowRight className="w-4 h-4 text-indigo-600" />
              </div>
              <h3 className="font-semibold text-gray-800 mb-1">{t('dashboard.viewReports')}</h3>
              <p className="text-sm text-gray-600">{t('dashboard.companyReports')}</p>
            </button>
          </div>
        </div>

        {/* Recent Users */}
        <div className="bg-white rounded-lg shadow p-4 sm:p-6 border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-800 flex items-center">
              <FaUsers className="w-5 h-5 mr-2 text-blue-600" />
              {t('dashboard.recentUsers')}
            </h2>
            <div className="flex items-center gap-3">
              {companyStats.totalUsers > 0 && (
                <button
                  onClick={handleManageUsers}
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1 bg-transparent hover:bg-blue-50 px-2 py-1 rounded transition-colors"
                >
                  View All
                  <FaArrowRight className="w-3 h-3" />
                </button>
              )}
              <button
                onClick={handleRefresh}
                disabled={refreshing || loading}
                className="text-sm text-gray-600 hover:text-gray-700 font-medium flex items-center gap-1 bg-transparent hover:bg-gray-50 px-2 py-1 rounded transition-colors disabled:opacity-50"
                title="Refresh"
              >
                <FaSync className={`w-3 h-3 ${refreshing ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>

          {loading ? (
            <div className="space-y-3">
              <div className="h-16 bg-gray-100 rounded animate-pulse"></div>
              <div className="h-16 bg-gray-100 rounded animate-pulse"></div>
              <div className="h-16 bg-gray-100 rounded animate-pulse"></div>
            </div>
          ) : recentUsers.length > 0 ? (
            <div className="space-y-3">
              {recentUsers.map((user) => (
                <div
                  key={user.id}
                  onClick={handleManageUsers}
                  className="p-3 sm:p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow cursor-pointer hover:border-blue-300"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold border ${
                          !user.isDeleted && !user.isFirstLogin
                            ? 'bg-green-100 text-green-800 border-green-300'
                            : 'bg-yellow-100 text-yellow-800 border-yellow-300'
                        }`}>
                          {!user.isDeleted && !user.isFirstLogin ? 'Active' : 'Inactive'}
                        </span>
                        <span className="text-xs text-gray-500">{user.email}</span>
                      </div>
                      <h3 className="font-medium text-gray-800 mb-1 text-sm sm:text-base">
                        {user.name}
                      </h3>
                      {user.createdAt && (
                        <p className="text-xs text-gray-400">
                          Added: {new Date(user.createdAt).toLocaleDateString('en-US', { 
                            year: 'numeric', 
                            month: 'short', 
                            day: 'numeric'
                          })}
                        </p>
                      )}
                    </div>
                    <FaArrowRight className="w-4 h-4 text-gray-400 mt-1 sm:mt-0" />
                  </div>
                </div>
              ))}
            </div>
          ) : companyStats.totalUsers === 0 ? (
            <div className="text-center py-8">
              <FaUsers className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600 mb-2">{t('dashboard.noUsersYet')}</p>
              <p className="text-sm text-gray-500 mb-4">{t('dashboard.startByAddingTeamMember')}</p>
              <button
                onClick={handleAddUser}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                {t('dashboard.addFirstUser')}
              </button>
            </div>
          ) : (
            <div className="text-center py-8">
              <FaUsers className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600 mb-2">{t('dashboard.noRecentUsers')}</p>
              <p className="text-sm text-gray-500">{t('dashboard.usersWillAppear')}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HomeForCompanyAdmin;

